import { useEffect, useState } from 'react';
import axios from 'axios';
import { Grid3x3, CheckCircle, AlertCircle, MinusCircle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });

const CELL = {
  complete: { bg: 'rgba(52,211,153,0.14)', color: '#6ee7b7', icon: CheckCircle },
  partial:  { bg: 'rgba(251,191,36,0.14)', color: '#fcd34d', icon: MinusCircle },
  missing:  { bg: 'rgba(248,113,113,0.13)', color: '#fca5a5', icon: AlertCircle },
};

export function RequirementsMatrix({ onMessageGroup }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);

  useEffect(() => {
    axios.get(`${API}/counselor/requirements-matrix`, auth())
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex justify-center py-20" data-testid="matrix-loading">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-sky-300 border-t-transparent" />
    </div>
  );
  if (!data) return <p className="py-10 text-center text-sm text-slate-300">Requirements data unavailable.</p>;

  const rows = sortKey
    ? [...data.rows].sort((a, b) => {
        const rank = { missing: 0, partial: 1, complete: 2 };
        return rank[a.cells[sortKey].status] - rank[b.cells[sortKey].status];
      })
    : data.rows;

  return (
    <div className="space-y-5" data-testid="requirements-matrix">
      {/* Column summary, act on a whole failing requirement at once */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.column_summary.map(c => {
          const pct = data.total_students
            ? Math.round((c.on_track_count / data.total_students) * 100) : 0;
          return (
            <div key={c.key} data-testid={`matrix-summary-${c.key}`}
              className="rounded-2xl border p-4 backdrop-blur-xl"
              style={{ background: 'rgba(13,27,46,0.72)',
                       borderColor: c.off_track_count ? 'rgba(248,113,113,0.28)' : 'rgba(52,211,153,0.28)' }}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.13em] text-slate-300">{c.label}</p>
              <p className="mt-2 text-2xl font-bold tabular-nums"
                style={{ color: c.off_track_count ? '#fca5a5' : '#6ee7b7' }}>{pct}%</p>
              <p className="mt-0.5 text-[11px] text-slate-300">{c.off_track_count} off track</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setSortKey(sortKey === c.key ? null : c.key)}
                  data-testid={`matrix-sort-${c.key}`}
                  className="text-[11px] text-slate-300 hover:text-sky-300 transition-colors">
                  {sortKey === c.key ? 'Sorted' : 'Sort'}
                </button>
                {c.off_track_count > 0 && (
                  <button onClick={() => onMessageGroup(c.student_ids, c.label)}
                    data-testid={`matrix-message-${c.key}`}
                    className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-sky-300 hover:text-sky-200 transition-colors">
                    <Send className="h-3 w-3" /> Nudge {c.off_track_count}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* The grid */}
      <div className="overflow-x-auto rounded-2xl border border-white/10 backdrop-blur-xl"
        style={{ background: 'rgba(13,27,46,0.72)' }}>
        <table className="w-full min-w-[900px] text-left">
          <thead>
            <tr className="border-b border-white/[0.08]">
              <th className="sticky left-0 z-10 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.13em] text-slate-300"
                style={{ background: '#0b1c31' }}>
                <span className="flex items-center gap-1.5"><Grid3x3 className="h-3.5 w-3.5" /> Student</span>
              </th>
              {data.columns.map(c => (
                <th key={c.key} onClick={() => setSortKey(sortKey === c.key ? null : c.key)}
                  className="cursor-pointer px-3 py-3 text-center text-[10.5px] font-semibold uppercase
                    tracking-[0.1em] text-slate-300 hover:text-sky-300 transition-colors">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.student_id} className="border-b border-white/[0.04] hover:bg-white/[0.03]"
                data-testid={`matrix-row-${r.student_id}`}>
                <td className="sticky left-0 z-10 px-4 py-2.5" style={{ background: '#0b1c31' }}>
                  <p className="text-[13px] font-medium text-white">{r.name}</p>
                  <p className="text-[10.5px] text-slate-300">
                    Grade {r.grade || '-'} · {r.complete_count}/{data.columns.length} met
                  </p>
                </td>
                {data.columns.map(c => {
                  const cell = r.cells[c.key];
                  const cfg = CELL[cell.status];
                  const Icon = cfg.icon;
                  return (
                    <td key={c.key} className="px-2 py-2.5 text-center">
                      <span className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[10.5px] font-medium"
                        style={{ background: cfg.bg, color: cfg.color }}>
                        <Icon className="h-3 w-3" /> {cell.label}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
